import React, { useState } from 'react';
import { Card, Form, Modal, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { InfoCircle, CloseCircle, Add } from 'iconsax-react';
import MyApiClient from '../../../../../api-client/MyApiClient';
import { segnalaMancanti } from '../../../../components/Util/campiMancanti';

const emptyPlayer = () => ({ nome: '', cognome: '', email: '', telefono: '', birthdate: '' });

const InvitaPlayer = () => {
    const navigate = useNavigate();
    const [players, setPlayers] = useState([emptyPlayer()]);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [modalType, setModalType] = useState('success'); // 'success' | 'error'

    const addPlayer = () => setPlayers([...players, emptyPlayer()]);

    const removePlayer = (index) => {
        if (players.length > 1) {
            setPlayers(players.filter((_, i) => i !== index));
            const newErrors = { ...errors };
            Object.keys(newErrors).forEach(key => {
                if (key.startsWith(`${index}_`)) delete newErrors[key];
            });
            setErrors(newErrors);
        }
    };

    const updatePlayer = (index, field, value) => {
        const newPlayers = [...players];
        newPlayers[index][field] = value;
        setPlayers(newPlayers);

        const errorKey = `${index}_${field}`;
        if (errors[errorKey]) {
            const newErrors = { ...errors };
            delete newErrors[errorKey];
            setErrors(newErrors);
        }
    };

    const validatePlayers = () => {
        const mancanti = [];
        const add = (index, field, messaggio) => mancanti.push({ campo: `player-${index}-${field}`, messaggio });

        players.forEach((player, index) => {
            const riga = players.length > 1 ? ` (riga ${index + 1})` : '';
            if (!player.nome.trim()) add(index, 'nome', `Manca il nome${riga}`);
            if (!player.cognome.trim()) add(index, 'cognome', `Manca il cognome${riga}`);
            if (!player.telefono.trim()) add(index, 'telefono', `Manca il numero di telefono${riga}`);
            if (player.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(player.email)) {
                add(index, 'email', `Formato email non valido${riga}`);
            }

            // birthdate: valore nativo del campo date = yyyy-MM-dd
            if (!player.birthdate) {
                add(index, 'birthdate', `Manca la data di nascita${riga}`);
            } else {
                const birthDate = new Date(player.birthdate);
                birthDate.setHours(0, 0, 0, 0);
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                if (isNaN(birthDate.getTime())) {
                    add(index, 'birthdate', `Data non valida${riga}`);
                } else if (birthDate > today) {
                    add(index, 'birthdate', `La data di nascita non può essere nel futuro${riga}`);
                } else {
                    let age = today.getFullYear() - birthDate.getFullYear();
                    const monthDiff = today.getMonth() - birthDate.getMonth();
                    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;
                    if (age < 18) add(index, 'birthdate', `Il player deve essere maggiorenne (almeno 18 anni)${riga}`);
                }
            }
        });

        setErrors({});
        return segnalaMancanti(mancanti) === 0;
    };

    const handleSubmit = async () => {
        if (!validatePlayers()) return;

        setLoading(true);
        try {
            const playersData = players.map(player => ({
                nome: player.nome.trim(),
                cognome: player.cognome.trim(),
                email: player.email.trim() || null,
                telefono: player.telefono.trim(),
                birthdate: player.birthdate // già in formato yyyy-MM-dd
            }));

            const apiClient = new MyApiClient();
            const tokenDetailsString = sessionStorage.getItem('userDetails');
            if (tokenDetailsString) {
                const tokenDetails = JSON.parse(tokenDetailsString);
                const token = tokenDetails.idToken;

                const response = await apiClient.bulkCreatePlayers(token, playersData);

                if (response.error) {
                    const err = new Error('Errore dal server');
                    err.data = response;
                    throw err;
                }

                const { data } = response;
                const message = data.created > 0
                    ? `${data.created} player invitati con successo.`
                    : 'Operazione completata con successo.';

                setModalMessage(message);
                setModalType('success');
                setShowModal(true);
                setPlayers([emptyPlayer()]);
            } else {
                setModalMessage('Errore durante l\'invio degli inviti. Riprova.');
                setModalType('error');
                setShowModal(true);
            }
        } catch (error) {
            let errorHandled = false;

            if (error.data && error.data.error && error.data.error.errors && Array.isArray(error.data.error.errors)) {
                const newErrors = { ...errors };
                error.data.error.errors.forEach(validationError => {
                    const index = validationError.index;
                    if (typeof index === 'number' && index >= 0 && index < players.length) {
                        newErrors[`${index}_email`] = validationError.error;
                    }
                });
                setErrors(newErrors);
                setModalMessage('Alcuni player contengono errori di validazione. Controlla le righe evidenziate in rosso.');
                setModalType('error');
                setShowModal(true);
                errorHandled = true;
            }

            if (!errorHandled) {
                const errorMessage = (error.data && error.data.error && error.data.error.detail) || error.message || 'Errore durante l\'invio degli inviti. Riprova.';
                setModalMessage(errorMessage);
                setModalType('error');
                setShowModal(true);
            }
        } finally {
            setLoading(false);
        }
    };

    const closeModal = () => {
        setShowModal(false);
        if (modalType === 'success') navigate('/struttura/clienti/list');
    };

    return (
        <>
            {/* Banner informativo */}
            <div className="sid-info-banner">
                <span className="sid-info-banner__icon"><InfoCircle size={22} variant="Bulk" /></span>
                <div className="sid-info-banner__body">
                    <div className="sid-info-banner__title">Modalità di creazione</div>
                    <p><strong>Player con email:</strong> verranno creati direttamente nel sistema ed invitati a scaricare l'applicazione Sportid Player e completare la propria IDS (Identità Digitale Sportiva).</p>
                    <p><strong>Player senza email:</strong> riceveranno un invito via SMS per aggiungere la propria email a cui il sistema inoltrerà l'invito a scaricare l'applicazione e completare l'IDS.</p>
                </div>
            </div>

            <Card>
                <Card.Header>
                    <Card.Title>Inserimento in blocco</Card.Title>
                    <button type="button" className="btn sid-btn-ghost btn-sm" onClick={addPlayer}>
                        <Add size={16} className="me-1" /> Aggiungi riga
                    </button>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={(e) => e.preventDefault()}>
                        <div className="sid-bulk-head">
                            <label>Nome *</label>
                            <label>Cognome *</label>
                            <label>Email</label>
                            <label>Telefono *</label>
                            <label>Data Nascita *</label>
                            <span />
                        </div>

                        {players.map((player, index) => (
                            <div className="sid-bulk-row" key={index}>
                                <Form.Group>
                                    <Form.Control id={`player-${index}-nome`} type="text" placeholder="Nome" value={player.nome}
                                        onChange={(e) => updatePlayer(index, 'nome', e.target.value)}
                                        isInvalid={!!errors[`${index}_nome`]} />
                                    <Form.Control.Feedback type="invalid">{errors[`${index}_nome`]}</Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group>
                                    <Form.Control id={`player-${index}-cognome`} type="text" placeholder="Cognome" value={player.cognome}
                                        onChange={(e) => updatePlayer(index, 'cognome', e.target.value)}
                                        isInvalid={!!errors[`${index}_cognome`]} />
                                    <Form.Control.Feedback type="invalid">{errors[`${index}_cognome`]}</Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group>
                                    <Form.Control id={`player-${index}-email`} type="email" placeholder="Email" value={player.email}
                                        onChange={(e) => updatePlayer(index, 'email', e.target.value)}
                                        isInvalid={!!errors[`${index}_email`]} />
                                    <Form.Control.Feedback type="invalid">{errors[`${index}_email`]}</Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group>
                                    <Form.Control id={`player-${index}-telefono`} type="tel" placeholder="Telefono" value={player.telefono}
                                        onChange={(e) => updatePlayer(index, 'telefono', e.target.value)}
                                        isInvalid={!!errors[`${index}_telefono`]} />
                                    <Form.Control.Feedback type="invalid">{errors[`${index}_telefono`]}</Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group>
                                    <Form.Control id={`player-${index}-birthdate`} type="date" value={player.birthdate}
                                        onChange={(e) => updatePlayer(index, 'birthdate', e.target.value)}
                                        isInvalid={!!errors[`${index}_birthdate`]} />
                                    <Form.Control.Feedback type="invalid">{errors[`${index}_birthdate`]}</Form.Control.Feedback>
                                </Form.Group>
                                <button type="button" className="sid-del" onClick={() => removePlayer(index)}
                                    disabled={players.length === 1} aria-label="Rimuovi riga">
                                    <CloseCircle size={20} />
                                </button>
                            </div>
                        ))}
                    </Form>
                </Card.Body>
            </Card>

            <div className="d-flex justify-content-end gap-4 mt-4">
                <button type="button" className="btn sid-btn-ghost" onClick={() => navigate('/struttura/clienti/list')}>
                    Annulla
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                    {loading ? 'Invio in corso…' : 'Invia inviti'}
                </button>
            </div>

            {/* Modal messaggi */}
            <Modal show={showModal} onHide={closeModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{modalType === 'success' ? 'Successo' : 'Errore'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{modalMessage}</Modal.Body>
                <Modal.Footer className="justify-content-center">
                    <button type="button" className="btn btn-primary" onClick={closeModal}>Chiudi</button>
                </Modal.Footer>
            </Modal>

            {/* Modal caricamento */}
            <Modal show={loading} centered backdrop="static">
                <Modal.Body className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="sr-only">Loading...</span>
                    </Spinner>
                    <div className="mt-2">Invio inviti in corso…</div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default InvitaPlayer;
